import {useNavigate} from "react-router-dom";
import React, {useMemo, useState} from "react";
import {Box} from "@mui/material";

// design system
import {Dialog} from "../../../../designSystem/components/Dialog";

// widgets
import {LoadingMessageView} from "../../../../widgets/LoadingMessageView";
import {PlatformBottomNavbar} from "../../../../widgets/PlatformBottomNavbar";
import {ApplicationSurvey} from "../../../../widgets/ApplicationSurvey";
import {NoCasesMessageView} from "../../../../widgets/NoCasesMessageView";

// feature-local components
import {TabNavigator} from "../TabNavigator";
import {ApplicationList} from "../ApplicationList";

// hooks
import {useLineLogin} from "../../../../hooks/useLineLogin";
import {useCasesByUserLineId} from "../../hooks/useCasesByUserLineId";
import {useCasesPolling} from "../../hooks/useCasesPolling";

import {getLiffId} from "../../../../utils/liff";
import {filterCasesByTab} from "../../model/filterCasesByTab";
import {FilterTab, TABS} from "../../model/tabs";

enum MODAL_CONTENT {
  SURVEY = 'survey'
}

export const ApplicationListFeature = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<FilterTab>('pairing');
  const [surveyingDocId, setSurveyingDocId] = useState<string | null>(null);
  const [dialogContent, setDialogContent] = useState<MODAL_CONTENT | ''>('');

  const {lineIdToken} = useLineLogin({liffId: getLiffId('APPLICATION_PAGE')})
  const {cases, loading, refetch} = useCasesByUserLineId({ token: lineIdToken })

  useCasesPolling({
    fn: refetch,
    deps: [lineIdToken],
    enabled: !!lineIdToken,
    intervalsMs: 5000
  })

  // Filter cases based on selected tab
  const filteredCases = useMemo(
    () => filterCasesByTab(cases, activeTab),
    [cases, activeTab]
  )

  const handleSurveyDialogClose = async () => {
    setDialogContent('');
    await refetch();
  }

  const onClickBrowseButton = (item) => {
    if (item.status !== 'todo') {
      navigate(`/caseDetail/${item.id}`)
    }
  }

  const onClickSurveyButton = (item) => {
    setSurveyingDocId(item.id)
    setDialogContent(MODAL_CONTENT.SURVEY)
  }

  return (
    <React.Fragment>
      <Box>
        {/* Tab Navigation */}
        <TabNavigator
          activeTab={activeTab}
          onClickTab={(tabValue: FilterTab) => setActiveTab(tabValue)}
          tabs={TABS}
        />

        {/* Case List */}
        <Box className="flex flex-col bg-gray-100 px-4 py-4 overflow-y-scroll gap-4 h-[calc(100vh-173px)]">
          {loading
            ? (
              <LoadingMessageView />
            ) : filteredCases?.length === 0
              ? (
                <NoCasesMessageView />
              ) : (
                <ApplicationList
                  cases={filteredCases}
                  onClickBrowseButton={onClickBrowseButton}
                  onClickSurveyButton={onClickSurveyButton}
                />
              )
          }
        </Box>

        {/* Bottom Navbar */}
        <PlatformBottomNavbar
          onClickLeftButton={() => navigate('/platform')}
          selectedTab="list-application"
        />
      </Box>

      <Dialog isOpen={dialogContent !== ''}>
        <div className="flex flex-col gap-4">
          <ApplicationSurvey
            docId={surveyingDocId}
            onCloseDialog={handleSurveyDialogClose}
            userLineToken={lineIdToken}
          />
        </div>
      </Dialog>
    </React.Fragment>
  );
};
